/**
 * WebSocket message handlers for the Interview component
 * This module breaks down the large onmessage handler into manageable pieces
 */

/**
 * AudioStreamPlayer - Handles streaming audio chunks with buffering
 * Based on FRONTEND_STREAMING_TTS_GUIDE.md specifications
 */
class AudioStreamPlayer {
  constructor() {
    this.audioContext = null;
    this.chunks = [];
    this.isPlaying = false;
    this.currentSource = null;
    // Earliest timestamp at which playback may begin. Lets the JD-specific
    // grace-period defer streamed bot audio without losing buffered chunks.
    this.startNotBefore = 0;
    this.startTimer = null;
    // True once SOME audio path (streaming or synthesized_speech fallback)
    // has played for the current bot turn. The backend can send both for
    // the same reply, and post-OGG-fix both paths actually work — without
    // this gate the candidate hears the bot in echo. Reset on each new
    // bot_speaking_started.
    this.audioClaimedForTurn = false;
    // Same idea for the caption/message: whichever path (streaming via
    // bot_speaking_started, or synthesized_speech fallback) publishes the
    // bot's text first claims the turn. Without this, the streaming path
    // never set the caption and `latestAIMessage` stayed pinned to the last
    // synthesized turn — captions appeared to "freeze" once streaming TTS
    // started winning a turn.
    this.textClaimedForTurn = false;
  }

  async initialize() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 24000
      });
    }

    // Resume audio context if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  setStartDelay(ms) {
    // Push out the earliest playback start. Chunks added in the meantime
    // accumulate in the buffer and play in order once the delay elapses.
    this.startNotBefore = Date.now() + ms;
  }

  async addChunk(base64Audio, format = 'audio/ogg') {
    // Buffer-only path. We deliberately do NOT play individual chunks here:
    // streamed OGG/Opus fragments aren't standalone-playable (the container
    // header lives in the first chunk only), so per-chunk Audio element
    // playback throws NotSupportedError on chunks 2+. We accumulate the
    // bytes and play once in finalize() with all chunks concatenated.
    try {
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      this.chunks.push({ bytes, format });
      console.log(`📥 Audio chunk buffered (${bytes.length} bytes, ${this.chunks.length} chunks in buffer)`);
    } catch (error) {
      console.error('❌ Failed to buffer audio chunk:', error);
    }
  }

  async playNextChunk() {
    // Retained for backward compatibility — no-op in buffer-then-play mode.
    // Real playback happens in finalize().
  }

  reset() {
    this.chunks = [];
    this.isPlaying = false;
    this.startNotBefore = 0;
    this.audioClaimedForTurn = false;
    this.textClaimedForTurn = false;
    if (this.startTimer) {
      clearTimeout(this.startTimer);
      this.startTimer = null;
    }
    if (this.currentSource) {
      try {
        this.currentSource.stop();
      } catch (e) {
        // Ignore if already stopped
      }
      this.currentSource = null;
    }
    console.log('🔄 Audio player reset');
  }

  async finalize(callback) {
    // Concatenate all buffered chunks into one blob and play once.
    // OGG container headers live in the first chunk; subsequent chunks
    // are valid continuation pages. Concatenating in arrival order yields
    // a complete, decodable OGG stream.
    if (this.chunks.length === 0) {
      console.log('✅ Audio streaming finalized (no chunks buffered)');
      if (callback) callback();
      return;
    }

    // If the synthesized_speech fallback already played for this turn, do
    // not also play the streamed copy — that's the echo. Drop the buffer
    // and run the callback so the mic-resume flow still fires.
    if (this.audioClaimedForTurn) {
      console.log('🔇 Streaming finalize: audio already played by synthesized_speech for this turn, skipping');
      this.chunks = [];
      if (callback) callback();
      return;
    }
    this.audioClaimedForTurn = true;

    // Honor any pending JD-specific start-delay before playback begins.
    const remainingDelay = this.startNotBefore - Date.now();
    if (remainingDelay > 0) {
      console.log(`⏳ Holding playback for ${remainingDelay}ms (grace period)`);
      await new Promise((resolve) => setTimeout(resolve, remainingDelay));
    }

    const totalSize = this.chunks.reduce((sum, c) => sum + c.bytes.length, 0);
    const merged = new Uint8Array(totalSize);
    let offset = 0;
    const format = this.chunks[0].format;
    for (const chunk of this.chunks) {
      merged.set(chunk.bytes, offset);
      offset += chunk.bytes.length;
    }
    this.chunks = [];

    const blob = new Blob([merged], { type: format });
    const audioUrl = URL.createObjectURL(blob);
    const audio = new Audio(audioUrl);
    this.isPlaying = true;

    let finalized = false;
    const finish = (reason) => {
      if (finalized) return;
      finalized = true;
      try { URL.revokeObjectURL(audioUrl); } catch (_) {}
      this.isPlaying = false;
      console.log(`✅ Audio streaming finalized (${reason})`);
      if (callback) callback();
    };

    audio.onended = () => finish('ended');
    audio.onerror = (error) => {
      console.error('❌ Failed to play merged audio:', error);
      finish('error');
    };

    try {
      console.log(`▶️ Playing merged audio (${totalSize} bytes, format=${format})`);
      await audio.play();
    } catch (error) {
      console.error('❌ Failed to start merged audio playback:', error);
      finish('play_rejected');
    }
  }
}

// Last non-empty interim transcript text. Used as a fallback when the
// backend emits a `final_consolidated_transcript` with empty `transcript.text`
// (which would otherwise silently drop the candidate's just-spoken answer).
let lastInterimTranscriptText = '';

// Timestamp of the most recent non-empty interim transcript. Drives the
// continuation-hold gate in handleFinalConsolidatedTranscript: if the
// candidate is still actively producing words, the gate re-arms the defer
// instead of committing — that prevents the bot from interrupting an
// in-progress utterance even when the backend stops re-finalizing.
let lastInterimTranscriptAt = 0;

// Read-only accessor for code outside this module (e.g., the silence
// watchdog in InterviewRefactored) so it can avoid racing against an
// active defer.
export const isFinalTranscriptDeferPending = () =>
  pendingFinalTranscriptDefer?.timer != null;

// Track the most recent `video_processing_keepalive` from the media server
// pipeline. The server emits these every few seconds while it's assembling
// the recording. We use this to know "server is alive but still working"
// vs "server actually died" — and to surface a stalled processing state
// to the team without bothering the candidate.
let firstKeepaliveAt = 0;
let lastKeepaliveAt = 0;
let keepaliveStallTimer = null;
const VIDEO_PROCESSING_STALL_MS = 10 * 60 * 1000; // 10 minutes

const handleVideoProcessingKeepalive = (response, context) => {
  const now = Date.now();
  if (!firstKeepaliveAt) {
    firstKeepaliveAt = now;
    console.log('📹 Server entered video-processing phase (first keepalive)');
  }
  lastKeepaliveAt = now;
  const elapsedSec = Math.round((now - firstKeepaliveAt) / 1000);
  // Quiet log — every ~10s of processing — instead of one per keepalive.
  if (elapsedSec % 10 === 0) {
    console.log(`📹 Video processing in progress (${elapsedSec}s elapsed since first keepalive)`);
  }

  // Arm a stall watchdog. If we see keepalives but never `recording_completed`
  // within VIDEO_PROCESSING_STALL_MS, the server is hung — write a recovery
  // marker to localStorage so an operator script can sweep it up later.
  if (!keepaliveStallTimer) {
    keepaliveStallTimer = setTimeout(() => {
      const interviewSlotId = context?.getInterviewSlotId
        ? context.getInterviewSlotId()
        : (typeof localStorage !== 'undefined' ? localStorage.getItem('interviewSlotID') : null);
      console.error(`⏱️ Video processing stalled — no recording_completed after ${Math.round(VIDEO_PROCESSING_STALL_MS / 60000)} minutes`);
      try {
        const marker = {
          interviewSlotId,
          firstKeepaliveAt: new Date(firstKeepaliveAt).toISOString(),
          lastKeepaliveAt: new Date(lastKeepaliveAt).toISOString(),
          stalledAt: new Date().toISOString(),
          reason: 'media_server_no_completion_after_keepalive',
        };
        localStorage.setItem(
          `stalled_video_processing_${interviewSlotId || 'unknown'}_${Date.now()}`,
          JSON.stringify(marker)
        );
      } catch (_) { /* localStorage may be unavailable */ }
    }, VIDEO_PROCESSING_STALL_MS);
  }
};

// Reset keepalive tracking when a new bot turn begins or interview restarts.
// Exposed so other handlers can clear it if needed.
const resetKeepaliveTracking = () => {
  firstKeepaliveAt = 0;
  lastKeepaliveAt = 0;
  if (keepaliveStallTimer) {
    clearTimeout(keepaliveStallTimer);
    keepaliveStallTimer = null;
  }
};

// Global audio player instance for streaming
let globalAudioPlayer = null;

function getAudioPlayer() {
  if (!globalAudioPlayer) {
    globalAudioPlayer = new AudioStreamPlayer();
  }
  return globalAudioPlayer;
}

// Helper function to play audio from base64
const playAudioFromBase64 = (
  base64Audio,
  format = "audio/wav",
  text = "",
  onEndedCallback = null,
  setters = {}
) => {
  const {
    setShowLoader,
    setIsSpeaking,
    setBotSpeechCaption,
    setConversationTranscript
  } = setters;

  try {
    console.log('🎵 Playing audio from base64');
    
    const binaryString = atob(base64Audio);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: format });
    const audioUrl = URL.createObjectURL(blob);
    const audio = new Audio(audioUrl);
    
    audio.volume = 0.8;

    audio.onloadstart = () => {
      setShowLoader?.(false);
      setIsSpeaking?.(true);
      
      if (text) {
        setBotSpeechCaption?.(text);
        setConversationTranscript?.(prev => [
          ...prev,
          {
            type: "bot",
            text: text,
            timestamp: new Date().toLocaleTimeString(),
          },
        ]);
      }
    };

    audio.onended = () => {
      setIsSpeaking?.(false);
      setBotSpeechCaption?.("");
      URL.revokeObjectURL(audioUrl);
      
      if (onEndedCallback && typeof onEndedCallback === "function") {
        try {
          onEndedCallback();
        } catch (error) {
          console.error("🎵 Error executing audio callback:", error);
        }
      }
    };

    audio.onerror = (error) => {
      // Silently handle audio errors during normal operation
      // These often occur during normal audio transitions and don't indicate real problems
      setIsSpeaking?.(false);
      setBotSpeechCaption?.("");
      URL.revokeObjectURL(audioUrl);
    };

    audio.play().catch(error => {
      console.error('❌ Failed to play audio:', error);
      setIsSpeaking?.(false);
      setBotSpeechCaption?.("");
      URL.revokeObjectURL(audioUrl);
    });

  } catch (error) {
    console.error('❌ Error processing base64 audio:', error);
    setIsSpeaking?.(false);
  }
};

/**
 * Handle bot_speaking_started message (Streaming TTS)
 * ⚡ CRITICAL: Close microphone IMMEDIATELY when bot starts speaking
 */
export const handleBotSpeakingStarted = (response, context) => {
  const {
    audioRecording,
    setIsAITalking,
    setIsSpeaking,
    setLiveCaption,
    setStreamingTranscript,
    setShowLoader,
    setBotSpeechCaption,
    setMessages,
    setConversationTranscript,
    isTerminationInProgress,
    isInterviewTerminated,
    isInterviewCompleted,
    onNewBotQuestion
  } = context;

  // Don't process if interview is terminated
  if (isTerminationInProgress || isInterviewTerminated || isInterviewCompleted) {
    console.log('🚫 Ignoring bot_speaking_started - interview terminated');
    return;
  }

  // Tell the parent a new bot question is starting so it can reset the
  // per-question disconnect counter — the next disconnect should be
  // counted as the 1st (RED banner) again, not a stale carry-over.
  if (typeof onNewBotQuestion === 'function') {
    try { onNewBotQuestion(); } catch (err) { console.warn('⚠️ onNewBotQuestion threw:', err); }
  }

  console.log('🤖 Bot speaking started:', response.text);
  console.log('⏱️ Bot speaking started at:', Date.now());

  // Race guard: backend may dispatch bot_speaking_started while we're still
  // holding a final_consolidated_transcript in the defer-and-replace buffer.
  // If we let the deferred handler fire on its own timer it'll flip
  // showLoader=true on top of the already-playing bot audio (the "freeze"
  // symptom). Cancel the timer and commit the buffered transcript now,
  // with the loader path suppressed.
  const buffered = cancelPendingFinalTranscript('bot_speaking_started arrived');
  if (buffered) {
    buffered.response._suppressLoader = true;
    // _finalDeferApplied is already true on the buffered response (set in
    // the original defer block), so this re-entry falls straight through
    // to the storage/mic-close path.
    handleFinalConsolidatedTranscript(buffered.response, buffered.context);
  }

  // Cancel any pending mic-reopen from a previous bot turn. Otherwise its
  // timer could fire mid-speech and reopen the mic into this bot's audio.
  cancelPendingMicReopen('new bot_speaking_started');

  // ✅ HIDE LOADER IMMEDIATELY - Bot is responding now
  if (setShowLoader) {
    setShowLoader(false);
    console.log('🎬 Loader hidden - bot started speaking');
  }

  // ✅ CLOSE MIC IMMEDIATELY - This is the key optimization!
  // Use suspendRecording to pause without tearing down the audio pipeline
  if (audioRecording) {
    console.log('🔍 Mic state check:', {
      isRecording: audioRecording.isRecording,
      isStarting: audioRecording.isStarting,
      recordingState: audioRecording.recordingState
    });
    
    // Suspend recording if active (keeps pipeline alive for quick resume)
    if (audioRecording.isRecording || audioRecording.isStarting) {
      console.log('🔇 Suspending mic instantly (bot_speaking_started)');
      if (audioRecording.suspendRecording) {
        audioRecording.suspendRecording();
      } else {
        // Fallback to stopRecording if suspendRecording is not available
        console.warn('⚠️ suspendRecording not available, using stopRecording');
        audioRecording.stopRecording();
      }
    } else {
      console.log('ℹ️ Mic already inactive - state:', audioRecording.recordingState);
    }
  } else {
    console.warn('⚠️ audioRecording object not available');
  }

  // Clear any live captions/transcripts
  if (setLiveCaption) setLiveCaption('');
  if (setStreamingTranscript) setStreamingTranscript('');

  // Set AI talking state
  if (setIsAITalking) setIsAITalking(true);

  // Reset audio player for new streaming session
  const audioPlayer = getAudioPlayer();
  audioPlayer.reset();

  // Publish the new bot turn's caption + message HERE, regardless of which
  // audio path (streaming vs. synthesized_speech fallback) ends up playing.
  // Previously this was only done inside handleSynthesizedSpeech, so when
  // the streaming path won the turn the caption stayed frozen on the prior
  // turn's text and `latestAIMessage` never advanced. Mark
  // textClaimedForTurn so handleSynthesizedSpeech doesn't double-publish.
  const botText = response.text;
  if (botText && botText.trim()) {
    audioPlayer.textClaimedForTurn = true;

    if (setMessages) {
      setMessages(prev => [
        ...prev,
        { type: 'ai', text: botText, timestamp: Date.now() }
      ]);
    }

    if (setConversationTranscript) {
      setConversationTranscript(prev => [
        ...prev,
        { type: 'bot', text: botText, timestamp: new Date().toLocaleTimeString() }
      ]);
    }

    // Drives the typing animation (setBotSpeechCaption is wired to
    // startTypingAnimation in the component).
    if (setBotSpeechCaption) setBotSpeechCaption(botText);

    // Surface the typing animation in transcriptOverlayText, which only
    // shows typingText/fullTypingText while isSpeaking is true.
    if (setIsSpeaking) setIsSpeaking(true);
  }

};

/**
 * Handle audio_chunk message (Streaming TTS)
 * Buffers and plays audio chunks as they arrive
 */
export const handleAudioChunk = async (response, context) => {
  const {
    audioRecording,
    setIsAITalking,
    setIsSpeaking,
    setBotSpeechCaption,
    isTerminationInProgress,
    isInterviewTerminated,
    isInterviewCompleted
  } = context;

  // Don't process if interview is terminated
  if (isTerminationInProgress || isInterviewTerminated || isInterviewCompleted) {
    console.log('🚫 Ignoring audio_chunk - interview terminated');
    return;
  }

  const audioPlayer = getAudioPlayer();

  // Check if this is the final chunk
  if (response.is_final) {
    console.log('✅ Final audio chunk received - streaming complete');
    
    // Finalize playback and reopen mic after completion
    await audioPlayer.finalize(() => {
      console.log('🎤 Reopening mic after streaming complete');

      if (setIsAITalking) setIsAITalking(false);
      // Caption was published in bot_speaking_started for the streaming path;
      // mirror the synthesized_speech finalize behavior so the typing
      // animation cleans up and the overlay falls back to latestAIMessage.
      if (setIsSpeaking) setIsSpeaking(false);
      if (setBotSpeechCaption) setBotSpeechCaption('');
      
      // Schedule mic reopen via the cancellable helper. If a new
      // bot_speaking_started lands during this 500ms window we drop the
      // reopen instead of opening the mic into the bot's next utterance.
      // resumeRecording self-heals: it issues a cheap worklet RESUME when
      // the pipeline is still up, or runs a full startRecording when it's
      // been torn down.
      scheduleMicReopen(500, async () => {
        if (!audioRecording) {
          console.warn('⚠️ audioRecording object not available at reopen time');
          return;
        }
        console.log('🔍 Reopening mic - state:', {
          isRecording: audioRecording.isRecording,
          isSuspended: audioRecording.isSuspended,
          recordingState: audioRecording.recordingState
        });
        try {
          const success = await audioRecording.resumeRecording();
          console.log('🎤 Mic reopened (post streaming TTS):', success ? 'successful' : 'failed');
        } catch (error) {
          console.error('❌ Failed to reopen mic:', error);
        }
      });
    });
    return;
  }

  // Process audio chunk if present
  if (response.audio && response.audio.length > 0) {
    const format = response.format || 'audio/ogg';
    const codec = response.codec || 'opus';
    const fullFormat = codec ? `${format};codecs=${codec}` : format;
    
    console.log(`📥 Audio chunk received (format: ${fullFormat}, size: ${response.audio.length})`);
    
    // Add chunk to player buffer
    await audioPlayer.addChunk(response.audio, fullFormat);
  }
};

/**
 * Handle synthesized speech messages (Fallback for complete audio)
 */
export const handleSynthesizedSpeech = (response, context) => {
  const {
    setMessages,
    playAudio,
    isTerminationInProgress,
    isInterviewTerminated,
    isInterviewCompleted,
    audioRecording,
    setIsAITalking,
    hasReceivedInitialResponse,
    setHasReceivedInitialResponse,
    sessionId,
    setLiveCaption,
    setShowLoader
  } = context;

  // Don't play synthesized audio if termination/completion is in progress
  if (isTerminationInProgress || isInterviewTerminated || isInterviewCompleted) {
    return;
  }

  // Echo-prevention: if the streaming audio_chunk path already played for
  // this bot turn, skip the synthesized_speech fallback. The backend sends
  // both for the same reply; without this gate the candidate hears it twice.
  // Placed AFTER the JD grace defer so the second (post-2s) re-entry
  // re-evaluates the flag — by then the streaming path has often already
  // claimed the turn.
  const _audioPlayer = getAudioPlayer();
  if (_audioPlayer.audioClaimedForTurn) {
    console.log('🔇 synthesized_speech: streaming path already played for this turn, skipping');
    return;
  }
  _audioPlayer.audioClaimedForTurn = true;

  // Check for session information to prevent duplicate responses
  const responseSessionId = response.session_id;
  const isReconnection = response.is_reconnection_response;
  
  // If this is a reconnection response and we've already received initial content, skip it
  if (isReconnection && hasReceivedInitialResponse) {
    console.log('🔄 Skipping duplicate AI response after reconnection');
    return;
  }
  
  // If this is the first response, mark it as received
  if (!hasReceivedInitialResponse) {
    setHasReceivedInitialResponse(true);
    console.log('✅ First AI response received and marked');
  }

  console.log('🎵 Received synthesized audio (fallback mode), suspending recording and playing');
  
  // ✅ HIDE LOADER IMMEDIATELY - Bot is responding now
  if (setShowLoader) {
    setShowLoader(false);
    console.log('🎬 Loader hidden - bot started speaking (fallback mode)');
  }
  
  // Clear live caption immediately when AI starts responding
  if (setLiveCaption) {
    setLiveCaption('');
    console.log('🧹 Cleared live caption for AI response');
  }
  
  // Suspend audio recording during AI speech
  if (audioRecording?.isRecording) {
    console.log('🔇 Suspending user audio recording during AI response');
    audioRecording.suspendRecording();
  }
  
  // Set AI talking state
  setIsAITalking?.(true);
  
  const format = response.format || "audio/wav";
  // If bot_speaking_started already published the caption + message for this
  // turn, suppress the text passed into playAudio so the typing animation
  // isn't restarted with the same string. Audio still plays normally.
  const textAlreadyClaimed = _audioPlayer.textClaimedForTurn === true;
  const speechText = textAlreadyClaimed
    ? ""
    : (response.text || "AI is responding...");

  // Enhanced playAudio with callback to resume recording
  const onAudioEnded = () => {
    console.log('🎤 AI finished speaking, scheduling mic reopen');
    setIsAITalking?.(false);

    if (!audioRecording) {
      console.warn('⚠️ audioRecording object not available');
      return;
    }
    // Cancellable schedule: if a new bot_speaking_started arrives in the
    // 800ms window, we won't open the mic into that bot's audio.
    // resumeRecording self-heals across torn-down vs suspended pipeline.
    scheduleMicReopen(800, async () => {
      try {
        const success = await audioRecording.resumeRecording();
        console.log('✅ Mic reopened (post synthesized speech):', success ? 'successful' : 'failed');
      } catch (error) {
        console.error('❌ Failed to reopen mic:', error);
      }
    });
  };

  playAudio(response.audio, format, speechText, onAudioEnded);

  // Add to chat if there's accompanying text and bot_speaking_started didn't
  // already publish it for this turn (otherwise we'd duplicate the message).
  if (response.text && !textAlreadyClaimed) {
    _audioPlayer.textClaimedForTurn = true;
    setMessages(prev => [
      ...prev,
      {
        type: "ai",
        text: response.text,
        session_id: responseSessionId,
        timestamp: Date.now()
      },
    ]);
  }
};

/**
 * Handle streaming interim transcript messages (new enhanced format)
 */
export const handleStreamingInterimTranscript = (response, context) => {
  const {
    setStreamingTranscript,
    setLiveCaption,
    isTerminationInProgress,
    isInterviewTerminated,
    isInterviewCompleted
  } = context;

  if (isTerminationInProgress || isInterviewTerminated || isInterviewCompleted) {
    console.log('🚫 Ignoring streaming transcript - interview terminated');
    return;
  }

  const transcript = response.transcript;
  
  if (!transcript || (typeof transcript === 'string' ? !transcript.trim() : !transcript.text)) {
    return;
  }

  // Handle both string and object transcript formats
  const transcriptText = typeof transcript === 'string' ? transcript : transcript.text;
  const confidence = typeof transcript === 'object' ? transcript.confidence : response.confidence;

  console.log('📝 Received streaming interim transcript:', transcriptText);

  // Remember the latest interim text so handleFinalConsolidatedTranscript
  // can fall back to it if the final arrives with empty text. Also stamp
  // the time so the continuation-hold gate can detect "candidate is still
  // talking right now" and refuse to commit the deferred final.
  if (transcriptText && transcriptText.trim()) {
    lastInterimTranscriptText = transcriptText;
    lastInterimTranscriptAt = Date.now();
  }

  // Update streaming transcript state - this should show in the UI
  if (setStreamingTranscript) {
    setStreamingTranscript(transcriptText);
  }
  
  // Also update live caption for backward compatibility
  if (setLiveCaption) {
    setLiveCaption(transcriptText);
  }
};

// Defer-and-replace state for final_consolidated_transcript handling.
//
// Backend Silero VAD fires final_consolidated_transcript on any pause >~1s,
// which truncates candidates who pause mid-thought. We hold the entire
// handler (mic close + transcript storage + bot trigger). If a newer
// final_consolidated_transcript arrives during the wait (backend got more
// audio and re-finalized with a more complete answer), we throw away the
// stale one and reset the timer. Cancellation is keyed off this discrete
// backend signal — not audio chunks — which avoids the noise-induced loop
// the earlier has_speech-based cancellation had.
//
// Unified across all interview types (JD-specific and general/screening).
// Earlier versions split this into per-type values, but parity testing
// showed screening candidates also need long pause tolerance — splitting
// just created two slightly-different behaviors that diverged over time.
let pendingFinalTranscriptDefer = null;
// Product spec (Pranav, 2026-05-22, commit ad48978): real candidates pause
// 4s+ mid-thought, so the bot must not commit a final inside that window.
// 6s defer + 7s backend hint covers both quiet thinkers and slower talkers.
// Do not shorten without product sign-off — the 3s value reverted into here
// in commit 56a8c54 caused the "bot cuts candidate mid-answer" regression.
const FINAL_TRANSCRIPT_DEFER_MS = 6000;

// Continuation-hold gate. When the defer timer fires we look back this far
// for evidence the candidate is still speaking (interim transcript update or
// speech-bearing audio chunk). If we find any, we re-arm the defer instead
// of committing — that's what makes "bot never interrupts" structural rather
// than just "wait and hope." Sized close to the defer width so any resumption
// mid-defer is caught (no dead zone). The earlier 1500ms-inside-4000ms value
// left a gap where 3s pauses were cut despite active resumption.
const CONTINUATION_HOLD_MS = 4000;

// Cap re-arms so a noisy room (or a stuck `has_speech` signal) cannot pin
// the conversation in a forever-deferred state. 8 re-arms ≈ 28s of
// extended speech with sub-3s pauses before the safety cap fires.
const MAX_CONTINUATION_REARMS = 8;

// Pending mic-reopen timer, scheduled by handleAudioChunk's finalize and
// handleSynthesizedSpeech's onAudioEnded. Kept module-level so a fresh
// bot_speaking_started can cancel an earlier turn's reopen before it
// fires — without this the mic could open into the new bot's audio.
let pendingMicReopenTimer = null;

const cancelPendingMicReopen = (reason) => {
  if (pendingMicReopenTimer) {
    console.log(`🛑 Cancelling pending mic reopen (${reason})`);
    clearTimeout(pendingMicReopenTimer);
    pendingMicReopenTimer = null;
  }
};

const scheduleMicReopen = (delayMs, fn) => {
  cancelPendingMicReopen('superseded by new schedule');
  pendingMicReopenTimer = setTimeout(async () => {
    pendingMicReopenTimer = null;
    try {
      await fn();
    } catch (err) {
      console.error('❌ Mic reopen handler threw:', err);
    }
  }, delayMs);
};

// Cancel a buffered final_consolidated_transcript when bot_speaking_started
// arrives. We *commit* the buffered transcript synchronously (so the
// candidate's answer isn't lost) but mark `_suppressLoader` so the deferred
// handler doesn't flip showLoader=true on top of the already-playing bot
// audio — that flip was the "freeze" symptom.
const cancelPendingFinalTranscript = (reason) => {
  if (!pendingFinalTranscriptDefer?.timer) return null;
  console.log(`🛑 Cancelling buffered final_consolidated_transcript (${reason})`);
  clearTimeout(pendingFinalTranscriptDefer.timer);
  const { response, context } = pendingFinalTranscriptDefer;
  pendingFinalTranscriptDefer = null;
  return { response, context };
};

/**
 * Handle final consolidated transcript messages (new enhanced format)
 * 🎤 CRITICAL: Microphone closure happens FIRST, regardless of transcript content
 */
export const handleFinalConsolidatedTranscript = (response, context) => {
  // Defer once per response. The marker is set so the re-entry (after the
  // setTimeout) skips the defer block and runs the real handler.
  if (!response._finalDeferApplied) {
    response._finalDeferApplied = true;

    if (pendingFinalTranscriptDefer?.timer) {
      console.log('🔄 Newer final_consolidated_transcript received — replacing buffered one');
      clearTimeout(pendingFinalTranscriptDefer.timer);
    }

    const deferMs = FINAL_TRANSCRIPT_DEFER_MS;
    const holdMs = CONTINUATION_HOLD_MS;
    console.log(`⏳ Holding final_consolidated_transcript for ${deferMs}ms (mic stays open for continuation)`);

    // Track re-arm count across continuation-hold extensions so we can stop
    // looping if a noisy environment keeps tripping the gate.
    const rearmCount = pendingFinalTranscriptDefer?.rearmCount || 0;

    const fire = () => {
      // Continuation-hold gate: before we commit (close mic, store transcript,
      // trigger bot), check whether the candidate is *currently* still
      // speaking. Two independent signals:
      //   1. A non-empty interim transcript arrived within holdMs.
      //   2. A speech-bearing audio chunk was seen within holdMs (read via
      //      context.getLastSpeechAt — plumbed from InterviewRefactored).
      // If either is true, re-arm the defer instead of firing. This is the
      // structural "bot never interrupts active speech" guarantee.
      const now = Date.now();
      const sinceInterim = now - (lastInterimTranscriptAt || 0);
      const lastSpeechAt = typeof context?.getLastSpeechAt === 'function'
        ? context.getLastSpeechAt()
        : 0;
      const sinceSpeechChunk = now - (lastSpeechAt || 0);
      const stillSpeaking = sinceInterim < holdMs || sinceSpeechChunk < holdMs;

      if (stillSpeaking && rearmCount < MAX_CONTINUATION_REARMS) {
        console.log(`🛑 Continuation-hold: speech still active (sinceInterim=${sinceInterim}ms, sinceChunk=${sinceSpeechChunk}ms, holdMs=${holdMs}) — re-arming defer (rearm ${rearmCount + 1}/${MAX_CONTINUATION_REARMS})`);
        pendingFinalTranscriptDefer = {
          response,
          context,
          rearmCount: rearmCount + 1,
          timer: setTimeout(fire, deferMs)
        };
        return;
      }

      if (stillSpeaking) {
        console.warn(`⚠️ Continuation-hold: max re-arms (${MAX_CONTINUATION_REARMS}) reached while speech still detected — committing to avoid stall`);
      }

      const r = pendingFinalTranscriptDefer.response;
      const c = pendingFinalTranscriptDefer.context;
      pendingFinalTranscriptDefer = null;
      console.log('⏱️ Defer window elapsed — processing buffered final_consolidated_transcript');
      handleFinalConsolidatedTranscript(r, c);
    };

    pendingFinalTranscriptDefer = {
      response,
      context,
      rearmCount,
      timer: setTimeout(fire, deferMs)
    };

    return;
  }

  const {
    setStreamingTranscript,
    setLiveCaption,
    setMessages,
    setConversationTranscript,
    setShowLoader,
    setLoaderText,
    isTerminationInProgress,
    isInterviewCompleted,
    isInterviewTerminated,
    audioRecording // Access to microphone control
  } = context;

  // If we got here via the bot_speaking_started race-cancel path, the bot
  // is already speaking. We still want to store the candidate's transcript,
  // but we must NOT raise the "processing" loader on top of bot audio
  // (that's what the user saw as a freeze).
  const suppressLoader = response._suppressLoader === true;

  // 🎤 BACKEND-DRIVEN MIC CLOSURE: Backend detected speech end via Google STT VAD
  // ⚡ CLOSE MIC IMMEDIATELY - This happens FIRST, before any validation
  // The message type itself (final_consolidated_transcript) is the authoritative signal.
  if (audioRecording?.isRecording) {
    console.log('🎤 Backend signaled speech end - Auto-closing microphone');
    audioRecording.stopRecording();
  }

  // Clear streaming states immediately (regardless of transcript content)
  setStreamingTranscript("");
  setLiveCaption("");

  // Validate transcript content - only affects whether we store/display it.
  // If the backend's final is empty but we have a buffered interim, use that
  // so the candidate's spoken answer isn't silently dropped.
  let resolvedTranscript = response.transcript;
  if (!resolvedTranscript || !resolvedTranscript.text) {
    if (lastInterimTranscriptText) {
      console.warn('⚠️ Empty final_consolidated_transcript — falling back to last interim text');
      resolvedTranscript = { text: lastInterimTranscriptText, confidence: null };
    } else {
      console.warn('⚠️ Received final_consolidated_transcript with empty text and no buffered interim — nothing to store');
      if (!isTerminationInProgress && !isInterviewCompleted && !isInterviewTerminated && !suppressLoader) {
        setShowLoader(true);
        setLoaderText("");
      }
      return;
    }
  }
  // Reset for next turn so a stale interim from this turn doesn't bleed into the next.
  lastInterimTranscriptText = '';

  const transcript = resolvedTranscript;
  const consolidationInfo = response.consolidation_info;
  const qualityMetrics = response.quality_metrics;

  console.log('✅ Received final consolidated transcript:', {
    text: transcript.text,
    confidence: transcript.confidence,
    wordCount: qualityMetrics?.word_count,
    consolidationMethod: consolidationInfo?.method,
    segments: consolidationInfo?.total_segments
  });
  
  // Add to chat and conversation transcript
  setMessages(prev => [
    ...prev,
    { 
      type: "user", 
      text: transcript.text,
      metadata: {
        confidence: transcript.confidence,
        wordCount: qualityMetrics?.word_count,
        consolidationInfo: consolidationInfo
      }
    }
  ]);

  setConversationTranscript(prev => [
    ...prev,
    {
      type: "user",
      text: transcript.text,
      timestamp: new Date().toLocaleTimeString(),
      confidence: transcript.confidence,
      qualityMetrics: qualityMetrics
    }
  ]);

  // Show processing loader for AI response — but skip when the bot is
  // already speaking (race-cancel path), otherwise the loader appears on
  // top of the bot's audio and looks like a freeze.
  if (!isTerminationInProgress && !isInterviewCompleted && !isInterviewTerminated && !suppressLoader) {
    setShowLoader(true);
    setLoaderText(""); // Using loader dots animation instead of text
  }
};

/**
 * Handle enhanced STT error messages
 */
export const handleEnhancedSTTError = (response, context) => {
  console.error('❌ Enhanced STT Error:', response.error);
  
  // For now, just log the error. In the future, we can add user-friendly error handling
  const error = new Error(`STT Error: ${response.error}`);
  
  // Optional error handlers if they exist in context
  if (context.setLastError) context.setLastError(error);
  if (context.onSTTError) context.onSTTError(error, response);
};
export const handleLiveCaption = (response, context) => {
  const {
    backendSTTEnabled,
    isTerminationInProgress,
    isInterviewTerminated,
    isInterviewCompleted,
    setLiveCaption
  } = context;

  if (!backendSTTEnabled || isTerminationInProgress || isInterviewTerminated || isInterviewCompleted) {
    return;
  }

  const newText = response.text || "";
  
  // Update live caption (simplified version)
  if (setLiveCaption && newText.trim()) {
    setLiveCaption(newText);
  }
  
  console.log('📝 Received live caption from backend:', response.text);
};

/**
 * Handle final transcript messages from backend STT
 */
export const handleTranscriptFinal = (response, context) => {
  const {
    backendSTTEnabled,
    setLiveCaption,
    setStreamingTranscript,
    setMessages,
    setConversationTranscript,
    setShowLoader,
    setLoaderText,
    isTerminationInProgress,
    isInterviewCompleted,
    isInterviewTerminated
  } = context;

  if (!backendSTTEnabled || !response.text) {
    return;
  }

  // Clear live captions
  if (setLiveCaption) setLiveCaption("");
  if (setStreamingTranscript) setStreamingTranscript("");
  
  // Add to chat and conversation transcript
  setMessages(prev => [
    ...prev,
    { type: "user", text: response.text }
  ]);

  setConversationTranscript(prev => [
    ...prev,
    {
      type: "user",
      text: response.text,
      timestamp: new Date().toLocaleTimeString(),
    }
  ]);

  // Show processing loader
  if (!isTerminationInProgress && !isInterviewCompleted && !isInterviewTerminated) {
    setShowLoader(true);
    setLoaderText("Processing your response...");
    setTimeout(() => setShowLoader(false), 1000);
  }

  console.log('✅ Received final transcript from backend:', response.text);
};

/**
 * Handle ping messages (health check)
 */
export const handlePing = (response, context) => {
  const { sendMessage } = context;
  
  const pongMessage = {
    type: "pong",
    timestamp: new Date().toISOString()
  };
  
  if (sendMessage) {
    sendMessage(pongMessage);
    console.log('🏓 Responded to ping with pong');
  }
};

/**
 * Handle video URL acknowledgment
 */
export const handleVideoUrlReceived = (response, context) => {
  const {
    setVideoUrlSent,
    setPendingVideoUrl,
    setVideoUrlRetryCount,
    setLoaderText,
    cleanupStoredVideoUrls,
    getInterviewSlotId
  } = context;

  console.log('✅ Backend confirmed video URL received:', response);
  
  setVideoUrlSent(true);
  setPendingVideoUrl(null);
  setVideoUrlRetryCount(0);

  // Update loader text to show completion
  setLoaderText("Video upload confirmed by backend!");

  // Stop all ongoing processes immediately
  if (window.stopS3UploadCheck) {
    console.log('🎥 Stopping S3 upload check due to video URL acknowledgment');
    window.stopS3UploadCheck();
    delete window.stopS3UploadCheck;
  }
  window.conclusionAcknowledgmentInProgress = false;

  // Clear any conclusion audio timeouts
  if (window.conclusionTimeoutId) {
    clearTimeout(window.conclusionTimeoutId);
    window.conclusionTimeoutId = null;
  }

  // Clean up any stored fallback data
  const interviewId = response.interview_id || getInterviewSlotId();
  if (interviewId) {
    cleanupStoredVideoUrls(interviewId);
  }

  // Store acknowledgment in localStorage to prevent re-sending across sessions
  try {
    localStorage.setItem(`video_url_acknowledged_${interviewId}`, "true");
  } catch (error) {
    console.warn("Could not store video URL acknowledgment:", error);
  }

  console.log('🎥 Video URL acknowledgment processed, stopping retries');
};

/**
 * Handle conclusion message (final speech before termination)
 */
export const handleConclusionMessage = (response, context) => {
  const {
    setIsConclusionAudioPlaying,
    stopAllRecording,
    stopScreenRecording,
    setMessages,
    playAudio,
    createConclusionAcknowledgment
  } = context;

  console.log('🎵 Received conclusion message');

  // Set conclusion audio playing state to prevent video URL retries
  setIsConclusionAudioPlaying(true);

  // Stop all recording activities
  stopAllRecording();
  stopScreenRecording();

  // Store conclusion text in chat
  if (response.text) {
    setMessages(prev => [
      ...prev,
      {
        type: "ai",
        text: response.text,
      },
    ]);
  }

  // Create and execute conclusion acknowledgment
  const sendConclusionAcknowledment = createConclusionAcknowledgment();

  // Play conclusion audio with acknowledgment callback
  if (response.audio) {
    const format = response.format || "audio/wav";
    playAudio(response.audio, format, response.text, sendConclusionAcknowledment);

    // Fallback timeout in case audio doesn't trigger callback
    window.conclusionTimeoutId = setTimeout(async () => {
      console.log('🎵 Fallback: Executing conclusion acknowledgment after timeout');
      await sendConclusionAcknowledment();
      window.conclusionTimeoutId = null;
    }, 90000); // 90 second fallback
  } else {
    // If no audio, send acknowledgment immediately
    setIsConclusionAudioPlaying(false);
    sendConclusionAcknowledment();
  }
};

/**
 * Handle interview completion confirmation
 */
export const handleInterviewCompleted = (response, context) => {
  const {
    setShowLoader,
    setLoaderText,
    clearAllRetries,
    stopAllAudio,
    disconnectWebSocket,
    cleanupStoredVideoUrls,
    getInterviewSlotId
  } = context;

  console.log('🏁 Interview completion confirmed by backend:', response);

  // Stop any ongoing processes
  if (window.stopS3UploadCheck) {
    window.stopS3UploadCheck();
    delete window.stopS3UploadCheck;
  }
  window.conclusionAcknowledgmentInProgress = false;

  // Clear all retries and timeouts
  if (clearAllRetries) clearAllRetries();

  if (window.conclusionTimeoutId) {
    clearTimeout(window.conclusionTimeoutId);
    window.conclusionTimeoutId = null;
  }

  // Update UI
  if (setLoaderText) setLoaderText("Interview successfully completed!");
  if (setShowLoader) setTimeout(() => setShowLoader(false), 2000);

  // Clean up resources
  if (stopAllAudio) stopAllAudio();
  
  // Clean up stored video URLs
  if (cleanupStoredVideoUrls && getInterviewSlotId) {
    const interviewId = response.interview_slot_id || getInterviewSlotId();
    cleanupStoredVideoUrls(interviewId);
  }

  // Disconnect WebSocket after a delay
  if (disconnectWebSocket) {
    setTimeout(() => {
      disconnectWebSocket();
    }, 1000);
  }
};

/**
 * Handle evaluation response from bot
 */
export const handleEvaluationComplete = (response, context) => {
  const { storeEvaluationResult } = context;

  console.log('📊 Received evaluation from bot:', response);
  
  const evaluationData = response.evaluation || response;
  
  // Store the evaluation
  if (storeEvaluationResult) {
    storeEvaluationResult(evaluationData).catch(error => {
      console.error('❌ Failed to store evaluation:', error);
    });
  }
};

/**
 * Main message router that delegates to specific handlers
 */
export const handleWebSocketMessage = (data, context) => {
  try {
    const response = typeof data === 'string' ? JSON.parse(data) : data;
    
    console.log(`📨 Processing WebSocket message: ${response.type}`);

    switch (response.type) {
      // ============ STREAMING TTS MESSAGES ============
      case 'bot_speaking_started':
        handleBotSpeakingStarted(response, context);
        break;
        
      case 'audio_chunk':
        handleAudioChunk(response, context);
        break;
        
      case 'synthesized_speech':
        // Fallback for complete audio (non-streaming)
        console.log('📦 Received complete audio (fallback mode)');
        handleSynthesizedSpeech(response, context);
        break;
        
      // ============ ENHANCED STT MESSAGES ============
      case 'streaming_interim_transcript':
      case 'interim_transcript':
        handleStreamingInterimTranscript(response, context);
        break;
        
      case 'final_consolidated_transcript':
        handleFinalConsolidatedTranscript(response, context);
        break;
        
      case 'enhanced_stt_error':
        handleEnhancedSTTError(response, context);
        break;
        
      // Legacy STT message types (for backward compatibility)
      case 'live_caption':
        handleLiveCaption(response, context);
        break;
        
      case 'transcript_final':
        handleTranscriptFinal(response, context);
        break;
        
      case 'ping':
        handlePing(response, context);
        break;
        
      case 'transcript_received':
        // Handle backend acknowledgment of received transcript
        console.log('✅ Backend acknowledged transcript receipt:', response);
        break;
        
      case 'video_url_received':
        handleVideoUrlReceived(response, context);
        break;
        
      case 'conclusion_message':
        handleConclusionMessage(response, context);
        break;
        
      case 'interview_completed':
        handleInterviewCompleted(response, context);
        break;
        
      case 'evaluation_complete':
      case 'evaluation':
        handleEvaluationComplete(response, context);
        break;
        
      case 'bot_response':
        // Handle bot_response - this might contain text or other data
        console.log('🤖 Received bot response:', response);
        if (response.text) {
          const { setMessages } = context;
          setMessages(prev => [
            ...prev,
            {
              type: "ai",
              text: response.text,
              timestamp: Date.now()
            },
          ]);
        }
        break;
        
      case 'video_processing_keepalive':
        handleVideoProcessingKeepalive(response, context);
        break;

      case 'video_processing_complete':
        // Some backend builds emit this on the interview socket once the
        // media-server pipeline finishes. Clear the stall watchdog so we
        // don't write a false-positive recovery marker.
        resetKeepaliveTracking();
        console.log('✅ Backend signaled video processing complete');
        break;

      case 'connection_established':
        console.log('🔗 WebSocket connection established');
        break;

      case 'interview_started':
        console.log('🎬 Backend confirmed interview started');
        break;

      case 'resume_success':
        // Backend reattached the preserved bot_manager to the new socket.
        // No UI work required here — the bot will pick up from where it
        // was (re-ask question / ask candidate to repeat / move on) via
        // the normal message stream. We just log for observability.
        console.log('✅ Bot WS resume_success — bot_manager reattached:', response);
        break;

      case 'resume_failed':
        // Bot couldn't find a preserved bot_manager for this slot (window
        // expired or never existed). Tell the candidate what's wrong so
        // they don't sit there waiting for a bot that's never coming back.
        console.warn('❌ Bot WS resume_failed:', response);
        if (context.toast?.error) {
          const reason = response.reason || 'unknown';
          const msg = reason === 'expired'
            ? 'Your interview session expired. Please refresh to start a new one.'
            : 'Could not restore your interview session. Please refresh to retry.';
          context.toast.error(msg, { position: 'top-center', autoClose: 10000 });
        }
        break;

      case 'audio_stream_stalled':
        // Backend's audio_stall_watchdog noticed no live_audio_stream
        // chunks for ~25s and is about to close the WS as a safety
        // measure. Most common cause is Chrome silently throttling the
        // candidate's AudioContext / worklet despite state="running".
        // We force-restart our local pipeline NOW so that (a) chunks
        // resume before backend closes the WS, OR (b) the new pipeline
        // is ready immediately after the reconnect lands. Either way,
        // the candidate's mic comes back without their intervention.
        console.warn(
          '🚨 Backend reported audio stream stalled:',
          response.message || `idle ${response.idle_seconds}s`
        );
        if (context.audioRecording?.forceRestartAudioPipeline) {
          context.audioRecording
            .forceRestartAudioPipeline('audio_stream_stalled_from_backend')
            .catch((err) =>
              console.error('❌ force-restart from audio_stream_stalled threw:', err)
            );
        } else {
          console.warn(
            '⚠️ forceRestartAudioPipeline not available on audioRecording — ' +
            'cannot self-heal from backend stall signal'
          );
        }
        break;

      case 'error':
        console.warn('⚠️ Backend error:', response.message || response.error || 'Unknown error');
        break;

      default:
        console.warn(`❓ Unknown message type: ${response.type}`);
        // Handle unknown message types gracefully
        if (response.evaluation) {
          handleEvaluationComplete(response, context);
        }
        break;
    }
  } catch (error) {
    console.error('❌ Error processing WebSocket message:', error);
    console.log('Raw message:', data);
    
    // Don't throw the error, just log it to prevent UI crashes
    return;
  }
};

export default {
  handleWebSocketMessage,
  handleBotSpeakingStarted,
  handleAudioChunk,
  handleSynthesizedSpeech,
  handleStreamingInterimTranscript,
  handleFinalConsolidatedTranscript,
  handleEnhancedSTTError,
  handleLiveCaption,
  handleTranscriptFinal,
  handlePing,
  handleVideoUrlReceived,
  handleConclusionMessage,
  handleInterviewCompleted,
  handleEvaluationComplete
};
